// GtShareCreate — build and send a redacted, OTP-gated candidate bundle to a client contact.
// Three panes in one modal: pick candidates, set recipient/scope, set the redaction map, then send.
// Backend: POST /api/shares. Compliance gate (withdrawn consent) is enforced server-side and here.
// C1-SHARECHECK: the share check (POST /api/shares/readiness) warns about anyone
// not interviewed by Nathan or with integrity flags not reviewed. It never
// blocks: Create share link always works. Consent is handled by the recruiter
// call, not here.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import GtLoader from '../../components/GtLoader';
import API_BASE from '../../config';
import { GtSelect } from '../../components/GtControls';
import GtAnonCards from '../../components/GtAnonCards'; // C2-ANON
import { SearchMatchLine } from '../../components/gtSearch'; // C1-SEARCH
import { TempDot, tempOf } from '../../components/gtTemp'; // C1-EMAIL

const PUBLIC_SHARE_PATH = '/s/'; // public route: work.gtco.io/s/:token

// Redaction map — mirrors server DEFAULT_VISIBILITY. Label + helptext per key.
const VIS_FIELDS = [
  { key: 'real_name',           label: 'Real name',            help: 'Off shows the candidate reference instead, e.g. GTC-CX7K2M.' },
  { key: 'headline',            label: 'Headline',             help: 'Professional one-liner.' },
  { key: 'summary',             label: 'Summary',              help: 'Profile summary.' },
  { key: 'location',            label: 'Location',             help: 'City / region.' },
  { key: 'skills',              label: 'Skills',               help: 'Skill tags.' },
  { key: 'right_to_work',       label: 'Right to work',        help: 'Work-authorisation status.' },
  { key: 'interview_scores',    label: 'Interview assessment', help: 'Overall result, summary, strengths and each competency with the evidence behind it.' },
  { key: 'interview_clips',     label: 'Interview clips',      help: 'Short clips you made from their interview, only where they agreed. Never shown when the real name is off.' },
  { key: 'interview_transcript',label: 'Interview transcript', help: 'Full interview text.' },
  { key: 'redacted_cv',         label: 'Career history',       help: 'Roles, education, projects and skills from the profile. Anything that identifies them is removed when the real name is off.' },
  { key: 'cv',                  label: 'CV file',              help: 'The original CV document.' },
  { key: 'dossier_pdf',         label: 'PDF download',         help: 'Lets the client download each profile as a GTCO-branded PDF, following these settings.' },
  { key: 'contact_details',     label: 'Contact details',      help: 'Email and phone. Usually withheld until placement.' },
  { key: 'expected_rate',       label: 'Expected rate',        help: 'Salary / rate expectation.' },
  { key: 'form_answers',        label: 'Form answers',         help: 'Answers from one form they filled in. Pick the form and questions below. Email, phone and links never go to a client.' },
];
const DEFAULT_VIS = { real_name: true, contact_details: false, cv: false, redacted_cv: true, interview_scores: true, interview_transcript: false, interview_clips: true, expected_rate: false, right_to_work: true, location: true, headline: true, skills: true, summary: true, dossier_pdf: true, form_answers: false };

const EXPIRY_OPTS = [
  { value: '', label: 'No expiry' },
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
];

function initials(name) { return String(name || '?').trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'; }
const AVC = ['#1E5A7B','#2E7D32','#6A1B9A','#B8860B','#00695C','#C2410C','#374151','#9D174D','#1565C0','#4E342E','#5B6C1E','#0F766E','#B91C1C','#5D4037','#3949AB','#00838F'];
function avatarColour(name) { const s = String(name || '?'); let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return AVC[h % AVC.length]; }

// Which form answers the client sees: ONE form per share, picked from the
// forms these people answered. The form sent from this role's pipeline comes
// first and is picked by default; its questions that can go to a client start
// ticked.
function FormAnswerPicker({ authFetch, ids, roleId, pick, onChange, named }) {
  const [forms, setForms] = useState(null);
  const [err, setErr] = useState(null);
  const seeded = useRef(false);
  const key = ids.slice().sort((a, b) => a - b).join(',');
  useEffect(() => {
    if (!key) { setForms([]); return undefined; }
    let live = true;
    authFetch('/api/shares/form-options', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidate_ids: key.split(',').map(Number), role_id: /^\d+$/.test(String(roleId || '')) ? Number(roleId) : null }) })
      .then((r) => { if (live) { setForms(r.forms || []); setErr(null); } })
      .catch((e) => { if (live) { setErr(e.message); setForms([]); } });
    return () => { live = false; };
  }, [key, roleId, authFetch]);

  const allFor = (f) => f.questions.filter((q) => q.shareable).map((q) => q.key);
  const chosenId = Object.keys(pick)[0] ? Number(Object.keys(pick)[0]) : null;
  const chosen = (forms || []).find((f) => f.id === chosenId) || null;
  const choose = (id) => {
    const f = (forms || []).find((x) => String(x.id) === String(id));
    onChange(f ? { [f.id]: allFor(f) } : {});
  };
  useEffect(() => {
    if (!forms || seeded.current) return;
    seeded.current = true;
    if (!chosenId && forms.length) onChange({ [forms[0].id]: allFor(forms[0]) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms]);

  const toggle = (qk) => {
    if (!chosen) return;
    const cur = pick[chosen.id] || [];
    onChange({ [chosen.id]: cur.indexOf(qk) !== -1 ? cur.filter((x) => x !== qk) : cur.concat([qk]) });
  };

  if (forms === null) return <div className="gtsc-fa"><div className="gtsc-fa-none">Loading their form answers…</div></div>;
  if (!forms.length) return <div className="gtsc-fa"><div className="gtsc-fa-none">{err || 'None of these people have answered a form yet, so there is nothing to show.'}</div></div>;
  const opts = forms.map((f) => ({ value: String(f.id), label: f.title + ' · ' + f.answered + ' of ' + ids.length + ' answered' + (f.sent_for_role ? ' · sent for this role' : '') }));
  const picked = chosen ? (pick[chosen.id] || []).length : 0;
  return (
    <div className="gtsc-fa">
      {err && <div className="gtsc-err">{err}</div>}
      <div className="gtsc-fa-pick"><span>Form</span><div className="gtsc-fa-sel"><GtSelect value={chosen ? String(chosen.id) : ''} options={opts} onChange={choose} /></div></div>
      {chosen && (
        <div className="gtsc-fa-form">
          {picked === 0 && <div className="gtsc-fa-warn">No questions ticked, so the client sees no answers.</div>}
          {chosen.questions.map((q) => {
            const on = (pick[chosen.id] || []).indexOf(q.key) !== -1;
            return (
              <label key={q.key} className={'gtsc-fa-q' + (q.shareable ? '' : ' gtsc-fa-q--off')}>
                <input type="checkbox" checked={q.shareable && on} disabled={!q.shareable} onChange={() => toggle(q.key)} />
                <span className="gtsc-fa-l">{q.label}</span>
                {q.why && (q.shareable ? (!named && <em className="gtsc-fa-why">{q.why}</em>) : <em className="gtsc-fa-why">{q.why}</em>)}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// C1-BENCHX: fromLabel says where preselected people came from (a role's pipeline or a bench).
export default function GtShareCreate({ clientId, client, roleId, getToken, onClose, onCreated, preselected, fromLabel }) {
  // Opened from a role's pipeline with people already ticked: start at the recipient.
  const [step, setStep] = useState(preselected && preselected.length ? 2 : 1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // candidate picker
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [picked, setPicked] = useState(() => {
    const m = {};
    (preselected || []).forEach((r) => { if (r && r.id && r.consent_status !== 'withdrawn') m[r.id] = r; });
    return m;
  }); // id -> candidate row
  // C2-PICK: where to pick from. A role's pipeline, a bench, or the whole pool.
  const [source, setSource] = useState(null); // 'role' | 'bench' | 'pool', chosen once data is in
  const [srcRole, setSrcRole] = useState(roleId ? String(roleId) : '');
  const [benches, setBenches] = useState(null);
  const [benchId, setBenchId] = useState('');
  const [readyOnly, setReadyOnly] = useState(false);
  const [warmOnly, setWarmOnly] = useState(false); // C1-EMAIL: hot or warm only

  // details
  const [contactId, setContactId] = useState('');
  const [scopeRoleId, setScopeRoleId] = useState(roleId ? String(roleId) : '');
  const [title, setTitle] = useState('');
  const [expiry, setExpiry] = useState('14');

  // redaction
  const [vis, setVis] = useState({ ...DEFAULT_VIS });
  // C2-ANON: standard (today's link) or anonymous (cards only, checked first)
  const [mode, setMode] = useState('standard');
  const [anonState, setAnonState] = useState({ clear: 0, total: 0, ready: false });

  // result
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  // share check: id -> { ready, reasons, checks }
  const [ready, setReady] = useState({});
  const [readyRole, setReadyRole] = useState(null);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, { ...opts, headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.message || body.error || body.detail || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  // The client page passes its client in. From a role, it is loaded here.
  const [loaded, setLoaded] = useState(null);
  const clientData = client || loaded;
  useEffect(() => {
    if (client || !clientId) return;
    authFetch('/api/clients/' + clientId).then((c) => setLoaded(c && c.client ? c.client : c)).catch((e) => setErr(e.message));
  }, [client, clientId, authFetch]);
  const contacts = useMemo(() => (clientData && clientData.contacts) || [], [clientData]);
  const roles = useMemo(() => (clientData && clientData.roles) || [], [clientData]);
  const contactOpts = useMemo(() => contacts.map(c => ({ value: String(c.id), label: c.full_name + (c.is_primary ? ' (primary)' : '') + (c.email ? ' · ' + c.email : '') })), [contacts]);
  // Opened from a role page, the role comes in as its GTC- reference. The share
  // needs the role's number, or it goes out with no role and starts no
  // submission. Resolve it once: from the client's roles, else from the role.
  const [extraRole, setExtraRole] = useState(null);
  useEffect(() => {
    if (!roleId || /^\d+$/.test(String(roleId))) return undefined;
    const want = String(roleId).toUpperCase();
    const hit = roles.find((r) => r.ref && String(r.ref).toUpperCase() === want);
    if (hit) { setScopeRoleId(String(hit.id)); return undefined; }
    let live = true;
    authFetch('/api/roles/' + encodeURIComponent(roleId)).then((r) => {
      const x = (r && (r.role || r.data)) || r;
      if (live && x && x.id) { setExtraRole({ id: x.id, title: x.title || String(roleId) }); setScopeRoleId(String(x.id)); }
    }).catch(() => {});
    return () => { live = false; };
  }, [roleId, roles, authFetch]);
  const roleOpts = useMemo(() => {
    const list = roles.map(r => ({ value: String(r.id), label: r.title }));
    if (extraRole && !roles.some((r) => String(r.id) === String(extraRole.id))) list.push({ value: String(extraRole.id), label: extraRole.title });
    return [{ value: '', label: 'Not role-specific' }, ...list];
  }, [roles, extraRole]);

  // default the recipient to the primary contact
  useEffect(() => {
    if (!contactId && contacts.length) {
      const p = contacts.find(c => c.is_primary) || contacts[0];
      if (p) setContactId(String(p.id));
    }
  }, [contacts, contactId]);

  // C2-PICK: benches, this client's first.
  useEffect(() => {
    let live = true;
    authFetch('/api/benches?status=active').then((r) => {
      if (!live) return;
      const cid = clientData && clientData.id;
      const list = (r.benches || []).slice().sort((a, b) => (b.client_id === cid) - (a.client_id === cid));
      setBenches(list);
    }).catch(() => { if (live) setBenches([]); });
    return () => { live = false; };
  }, [authFetch, clientData]);

  // Start where the people most likely are: this role, else this client's
  // open roles, else a bench for this client, else the whole pool.
  useEffect(() => {
    if (source || benches === null || (!clientData && clientId)) return;
    const cid = clientData && clientData.id;
    const openRoles = roles.filter((r) => r.status !== 'closed' && r.status !== 'filled');
    if (roleId || scopeRoleId) { setSource('role'); return; }
    if (openRoles.length) { setSrcRole(String(openRoles[0].id)); setSource('role'); return; }
    const own = benches.find((b) => b.client_id === cid);
    if (own) { setBenchId(String(own.id)); setSource('bench'); return; }
    setSource('pool');
  }, [source, benches, clientData, clientId, roles, roleId, scopeRoleId]);

  // Keep the role picked here and the role on the share in step with each other.
  useEffect(() => { if (scopeRoleId && !srcRole) setSrcRole(scopeRoleId); }, [scopeRoleId, srcRole]);

  const loadRows = useCallback(async () => {
    if (!source) return;
    setLoadingRows(true); setErr(null);
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const match = (c) => {
      if (!words.length) return true;
      const hay = [c.full_name, c.ref, c.headline, c.current_title, c.current_company, c.location, c.expertise_domain, c.stage].filter(Boolean).join(' ').toLowerCase();
      return words.every((w) => hay.indexOf(w) !== -1);
    };
    try {
      if (source === 'role') {
        const rid = srcRole || scopeRoleId || roleId;
        if (!rid) { setRows([]); setLoadingRows(false); return; }
        const r = await authFetch('/api/roles/' + rid + '/pipeline');
        setRows((r.pipeline || []).map(p => ({
          id: p.candidate_id, full_name: p.full_name, ref: p.candidate_ref, headline: p.headline,
          current_company: p.current_company, current_title: p.current_title, location: p.location,
          expertise_domain: p.expertise_domain, consent_status: p.consent_status, stage: p.stage,
          last_contacted_at: p.last_contacted_at, last_contact_kind: p.last_contact_kind, last_responded_at: p.last_responded_at, last_response_kind: p.last_response_kind, // C1-EMAIL
        })).filter(match));
      } else if (source === 'bench') {
        if (!benchId) { setRows([]); setLoadingRows(false); return; }
        const r = await authFetch('/api/benches/' + benchId);
        const buckets = {};
        (r.buckets || []).forEach((b) => { buckets[b.id] = b.label || b.name; });
        setRows((r.members || []).map((m) => ({
          id: m.candidate_id, full_name: m.full_name, ref: m.ref, headline: m.headline,
          current_company: m.current_company, current_title: m.current_title, location: m.location,
          expertise_domain: m.expertise_domain, consent_status: m.consent_status, stage: buckets[m.stage_id] || null,
          last_contacted_at: m.last_contacted_at, last_contact_kind: m.last_contact_kind, last_responded_at: m.last_responded_at, last_response_kind: m.last_response_kind, // C1-EMAIL
        })).filter(match));
      } else {
        const r = await authFetch('/api/shares/pick-search', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ q: query.trim() }) });
        setRows(r.rows || []);
      }
    } catch (e) { setErr(e.message); setRows([]); }
    setLoadingRows(false);
  }, [authFetch, query, source, srcRole, benchId, scopeRoleId, roleId]);

  useEffect(() => { const t = setTimeout(loadRows, 250); return () => clearTimeout(t); }, [loadRows]);

  // The share check runs for everyone on screen and everyone picked, against
  // the role on the share (or none). Results are cleared when the role changes.
  const checkRole = scopeRoleId || '';
  const checkIds = useMemo(() => {
    const s = new Set(rows.map((r) => r.id));
    Object.keys(picked).forEach((k) => s.add(Number(k)));
    return Array.from(s).filter((x) => Number.isInteger(x) && x > 0).sort((a, b) => a - b);
  }, [rows, picked]);
  const checkKey = checkRole + ':' + checkIds.join(',');
  useEffect(() => {
    if (!checkIds.length) return undefined;
    let live = true;
    const t = setTimeout(async () => {
      try {
        const r = await authFetch('/api/shares/readiness', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role_id: /^\d+$/.test(String(checkRole)) ? Number(checkRole) : null, candidate_ids: checkIds }) });
        if (!live) return;
        const m = {};
        (r.candidates || []).forEach((x) => { m[x.candidate_id] = x; });
        setReady((prev) => (readyRole === checkRole ? { ...prev, ...m } : m));
        setReadyRole(checkRole);
      } catch (e) { /* the check is a warning; a failure never stops a share */ }
    }, 300);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkKey, authFetch]);

  function togglePick(row) {
    if (row.consent_status === 'withdrawn') return; // hard block, mirrors server
    setPicked(prev => { const n = { ...prev }; if (n[row.id]) delete n[row.id]; else n[row.id] = row; return n; });
  }
  const pickedList = Object.values(picked);
  const checked = readyRole === checkRole;
  const notReady = checked ? pickedList.filter((c) => ready[c.id] && !ready[c.id].ready) : [];
  const allChecked = checked && pickedList.length > 0 && pickedList.every((c) => ready[c.id]);
  const noInterview = notReady.filter((c) => !ready[c.id].checks.interview.ok).length;
  const flagsOpen = notReady.filter((c) => ready[c.id].checks.interview.ok && !ready[c.id].checks.integrity.ok).length;

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const payload = {
        // Bodies must carry the real integer id. clientId may be a GTC- ref
        // (it comes from the URL), which only the path resolver understands.
        client_id: (clientData && clientData.id) ? clientData.id : clientId,
        role_id: /^\d+$/.test(String(scopeRoleId)) ? Number(scopeRoleId) : null,
        contact_id: contactId ? Number(contactId) : null,
        candidate_ids: pickedList.map(c => c.id),
        visibility: mode === 'anonymous' ? undefined : vis,
        mode,
        title: title.trim() || null,
        expires_in_days: expiry ? Number(expiry) : null,
      };
      const share = await authFetch('/api/shares', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      setCreated(share);
      setStep(4);
      onCreated && onCreated();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const publicUrl = created ? (window.location.origin + PUBLIC_SHARE_PATH + created.token) : '';
  function copyLink() { if (!publicUrl) return; navigator.clipboard.writeText(publicUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); }

  const canNext1 = pickedList.length > 0;
  const canNext2 = !!contactId;

  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtsh-modal" onClick={e => e.stopPropagation()}>
        <div className="gtc-modal-head">
          {step === 4 ? 'Share created' : 'Share candidate profiles'}
          {step < 4 && <span className="gtsh-steps">{['Candidates', 'Recipient', 'What they see'].map((s, i) => <span key={s} className={'gtsh-step' + (step === i + 1 ? ' gtsh-step--on' : '') + (step > i + 1 ? ' gtsh-step--done' : '')}>{i + 1}. {s}</span>)}</span>}
        </div>

        <div className="gtc-modal-body gtsh-body">
          {err && <div className="gtc-error gtsh-err">{err}</div>}

          {step === 1 && (
            <>
              <div className="gtsh-pickhead">
                <div className="gtpk-src" role="tablist" aria-label="Pick from">
                  {[['role', 'Role pipelines'], ['bench', 'Benches'], ['pool', 'Whole pool']].map(([k, l]) => (
                    <button key={k} type="button" role="tab" aria-selected={source === k} className={'gtpk-tab' + (source === k ? ' gtpk-tab--on' : '')} onClick={() => setSource(k)}>{l}</button>
                  ))}
                </div>
                {source === 'role' && (
                  roleOpts.length > 1
                    ? <div className="gtpk-sel"><GtSelect value={srcRole} options={[{ value: '', label: 'Pick a role' }].concat(roleOpts.slice(1))} onChange={(v) => { setSrcRole(v); if (v) setScopeRoleId(v); }} /></div>
                    : <div className="gtpk-hint">{clientData ? 'This client has no roles yet. Pick from a bench or the whole pool.' : 'Loading roles…'}</div>
                )}
                {source === 'bench' && (
                  benches && benches.length
                    ? <div className="gtpk-sel"><GtSelect value={benchId} options={[{ value: '', label: 'Pick a bench' }].concat(benches.map((b) => ({ value: String(b.id), label: b.name + ' · ' + b.member_count + (b.member_count === 1 ? ' person' : ' people') + (clientData && b.client_id === clientData.id ? ' · this client' : '') })))} onChange={setBenchId} /></div>
                    : <div className="gtpk-hint">{benches ? 'No benches yet.' : 'Loading benches…'}</div>
                )}
                <div className="gtpk-row">
                  <input className="gtsh-search" placeholder={source === 'pool' ? 'Search everyone: skills, title, company, place, name…' : 'Filter: skills, title, company, place, name…'} value={query} onChange={e => setQuery(e.target.value)} />
                  <label className="gtpk-ready"><input type="checkbox" checked={readyOnly} onChange={(e) => setReadyOnly(e.target.checked)} /><span>Ready only</span></label>
                  <label className="gtpk-ready"><input type="checkbox" checked={warmOnly} onChange={(e) => setWarmOnly(e.target.checked)} /><span>Hot or warm only</span></label>{/* C1-EMAIL */}
                </div>
                {source === 'pool' && !query.trim() && <div className="gtpk-hint">Showing the most recently updated people. Type anything to search the whole pool.</div>}
              </div>
              {pickedList.length > 0 && (
                <div className="gtpk-tray">
                  <span className="gtpk-tray-l">{pickedList.length} picked</span>
                  {pickedList.map((c) => <button type="button" key={c.id} className="gtpk-chip" onClick={() => togglePick(c)} title="Remove">{c.full_name || c.ref}<span aria-hidden="true">×</span></button>)}
                </div>
              )}
              <div className="gtsh-picklist">
                {(loadingRows || !source) ? <GtLoader compact /> : (() => {
                  const shown = rows
                    .filter((r) => !readyOnly || (checked && ready[r.id] && ready[r.id].ready))
                    .filter((r) => !warmOnly || ['hot', 'warm'].indexOf(tempOf(r).key) !== -1) // C1-EMAIL
                    .slice().sort((a, b) => (checked ? ((ready[b.id] && ready[b.id].ready ? 1 : 0) - (ready[a.id] && ready[a.id].ready ? 1 : 0)) : 0));
                  const pickable = shown.filter((r) => r.consent_status !== 'withdrawn' && !picked[r.id]);
                  if (!shown.length) {
                    return <div className="gtr-empty-sm">{source === 'role' && !srcRole ? 'Pick a role to see its pipeline.' : source === 'bench' && !benchId ? 'Pick a bench to see who is on it.' : readyOnly && rows.length ? 'No one here is ready yet. Untick Ready only to see everyone.' : warmOnly && rows.length ? 'No one here is hot or warm. Untick Hot or warm only to see everyone.' : 'No one matches.'}</div>;
                  }
                  return (<>
                    {source !== 'pool' && pickable.length > 1 && (
                      <button type="button" className="gtpk-all" onClick={() => setPicked((prev) => { const n = { ...prev }; pickable.forEach((r) => { n[r.id] = r; }); return n; })}>Select all {pickable.length} shown</button>
                    )}
                    {shown.map(row => {
                    const withdrawn = row.consent_status === 'withdrawn';
                    const on = !!picked[row.id];
                    return (
                      <button type="button" key={row.id} className={'gtsh-pickrow' + (on ? ' gtsh-pickrow--on' : '') + (withdrawn ? ' gtsh-pickrow--blocked' : '')} onClick={() => togglePick(row)} disabled={withdrawn}>
                        <span className="gtsh-check">{on ? '✓' : ''}</span>
                        <span className="gtsh-pickav" style={{ background: avatarColour(row.full_name) }}>{initials(row.full_name)}</span>
                        <span className="gtsh-pickbody">
                          <span className="gtsh-pickname">{row.full_name || 'Unnamed'}<TempDot row={row} />{row.ref && <span className="gtsh-pickref">{row.ref}</span>}</span>{/* C1-EMAIL */}
                          <span className="gtsh-picksub">{[row.current_title, row.current_company, row.location].filter(Boolean).join(' · ') || row.headline || '—'}</span>
                          {row.search_match && <SearchMatchLine m={row.search_match} />}
                        </span>
                        {withdrawn && <span className="gtsh-blocked">consent withdrawn</span>}
                        {row.stage && <span className="gtsh-stage">{row.stage}</span>}
                        {!withdrawn && checked && ready[row.id] && (
                          <span className={'gtsc-chip ' + (ready[row.id].ready ? 'gtsc-chip--ok' : 'gtsc-chip--warn')}
                            title={ready[row.id].ready ? 'Interviewed by Nathan, flags checked' : ready[row.id].reasons.join('\n')}>
                            {ready[row.id].ready ? 'Ready' : 'Not ready'}
                          </span>
                        )}
                      </button>
                    );
                    })}
                  </>);
                })()}
              </div>
            </>
          )}

          {step === 2 && (
            <div className="gtsh-form">
              <label className="gtc-field"><span>Send to contact</span>
                {contactOpts.length ? <GtSelect value={contactId} options={contactOpts} onChange={setContactId} /> : <div className="gtr-empty-sm">{clientData ? 'No contacts on this client. Add one on the client\'s page first.' : 'Loading contacts…'}</div>}
              </label>
              <label className="gtc-field"><span>Role (optional)</span><GtSelect value={scopeRoleId} options={roleOpts} onChange={setScopeRoleId} /></label>
              <label className="gtc-field"><span>Bundle title (optional)</span><input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Shortlist for GPU Kernel Engineer" /></label>
              <label className="gtc-field"><span>Link expiry</span><GtSelect value={expiry} options={EXPIRY_OPTS} onChange={setExpiry} /></label>
              <div className="gtsh-recap">{pickedList.length} candidate{pickedList.length === 1 ? '' : 's'} selected{preselected && preselected.length ? ' from ' + (fromLabel || 'the pipeline') : ''}. {scopeRoleId ? 'Anyone not yet with the client moves to Client review when you share.' : ''}{notReady.length ? ' ' + notReady.length + ' not ready yet.' : ''}</div>
            </div>
          )}

          {step === 3 && (
            <div className="gtsh-vis">
              {notReady.length > 0 && (
                <div className="gtsc-box gtsc-box--warn" role="status">
                  <div className="gtsc-box-h">
                    <b>{notReady.length} of {pickedList.length} not ready</b>
                    <span>
                      {[noInterview ? noInterview + ' not interviewed by Nathan' : null,
                        flagsOpen ? flagsOpen + ' with integrity flags not reviewed' : null].filter(Boolean).join(', ')}.
                      {' '}You can still share. Hover a name on step 1 to see why.
                    </span>
                  </div>
                </div>
              )}
              {notReady.length === 0 && allChecked && (
                <div className="gtsc-box gtsc-box--ok" role="status">
                  <b>{pickedList.length === 1 ? 'Ready' : 'All ' + pickedList.length + ' ready'}</b>
                  <span>Interviewed by Nathan, integrity flags checked.</span>
                </div>
              )}
              <div className="gtac-modes" role="radiogroup" aria-label="Type of link">
                <button type="button" role="radio" aria-checked={mode === 'standard'} className={'gtac-mode' + (mode === 'standard' ? ' gtac-mode--on' : '')} onClick={() => setMode('standard')}>
                  <b>Standard</b><span>Full profiles. You choose each field below.</span>
                </button>
                <button type="button" role="radio" aria-checked={mode === 'anonymous'} className={'gtac-mode' + (mode === 'anonymous' ? ' gtac-mode--on' : '')} onClick={() => setMode('anonymous')}>
                  <b>Anonymous</b><span>A card per person: seniority, company type, what they have done, salary band and availability. Nothing that finds them online.</span>
                </button>
              </div>
              {mode === 'anonymous' ? (
                <GtAnonCards authFetch={authFetch} people={pickedList} roleId={scopeRoleId} onStatus={setAnonState}
                  onLeaveOut={(id) => setPicked((prev) => { const n = { ...prev }; delete n[id]; return n; })} />
              ) : (<>
              <div className="gtsh-vis-intro">Choose what the client sees. Hidden fields never leave the server.</div>
              {VIS_FIELDS.map(f => (
                <label key={f.key} className={'gtsh-vistoggle' + (vis[f.key] ? ' gtsh-vistoggle--on' : '')}>
                  <span className="gtsh-vis-main">
                    <span className="gtsh-vis-label">{f.label}</span>
                    <span className="gtsh-vis-help">{f.help}</span>
                  </span>
                  <span className="gtsh-switch" onClick={(e) => { e.preventDefault(); setVis(v => ({ ...v, [f.key]: !v[f.key] })); }}>
                    <span className={'gtsh-switch-knob' + (vis[f.key] ? ' gtsh-switch-knob--on' : '')} />
                  </span>
                </label>
              ))}
              {vis.form_answers && (
                <FormAnswerPicker authFetch={authFetch} ids={pickedList.map((c) => c.id)} named={!!vis.real_name} roleId={checkRole}
                  pick={vis.form_answers_pick || {}} onChange={(p) => setVis((v) => ({ ...v, form_answers_pick: p }))} />
              )}
              </>)}
            </div>
          )}

          {step === 4 && created && (
            <div className="gtsh-done">
              <div className="gtsh-done-ref"><span className="gtr-refbadge">{created.ref}</span> is ready.</div>
              <p className="gtsh-done-copy">{created.mode === 'anonymous'
                ? 'Send this link to the contact. They verify with a one-time code, then see the anonymous cards only. A thumbs-up never releases a name: you do that from the Shares tab once terms are agreed and the person has said yes.'
                : 'Send this link to the contact. They verify with a one-time code, then see only the fields you allowed. Every open is tracked on the Shares tab.'}</p>
              <div className="gtsh-linkbox">
                <input readOnly value={publicUrl} onFocus={e => e.target.select()} />
                <button className="gtc-primary" onClick={copyLink}>{copied ? 'Copied' : 'Copy link'}</button>
              </div>
            </div>
          )}
        </div>

        <div className="gtc-modal-foot gtsh-foot">
          {step === 1 && <><span className="gtsh-count">{pickedList.length} selected</span><button className="gtc-btn-ghost" onClick={onClose}>Cancel</button><button className="gtc-primary" disabled={!canNext1} onClick={() => setStep(2)}>Next</button></>}
          {step === 2 && <><button className="gtc-btn-ghost" onClick={() => setStep(1)}>Back</button><button className="gtc-primary" disabled={!canNext2} onClick={() => setStep(3)}>Next</button></>}
          {step === 3 && <><button className="gtc-btn-ghost" onClick={() => setStep(2)}>Back</button>{mode === 'anonymous' && <span className="gtsh-count">{!pickedList.length ? 'No one picked' : !anonState.ready ? 'Writing cards…' : anonState.flagged ? anonState.flagged + ' worth a look' : 'All look clear'}</span>}<button className="gtc-primary" disabled={busy || (mode === 'anonymous' && (!anonState.ready || !pickedList.length))} onClick={submit}>{busy ? 'Creating…' : mode === 'anonymous' ? 'Create anonymous link' : 'Create share link'}</button></>}
          {step === 4 && <button className="gtc-primary" onClick={onClose}>Done</button>}
        </div>
      </div>
    </div>
  );
}
